import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Modal, Tab } from "semantic-ui-react";

import { DetailsCard } from "components";
import printJS from "lib/print-js";
import { COMMUNICATION_EVENTS } from "models";
import "modules/AppointmentDetails/components/CommunicationLog/CommunicationLog.scss";
import { AgreementModal, AppointmentInfo, TabContent } from "modules/AppointmentDetails/components/CommunicationLog/components";
import { CommunicationLogReplacementCar } from "modules/AppointmentDetails/components/CommunicationLogReplacementCar";

import type { Appointment, CommunicationAgreement } from "models";

type CommunicationLogProps = {
  appointment: Appointment;
};

type TabPaneType = {
  menuItem: string;
  render: () => any;
};

export enum COMMUNICATION_LOG_TAB {
  OnlineCheckin = 1,
  DiagnoseOverview,
  DiagnoseOverviewUpdate,
  RepairOverview
}

export const CommunicationLog = ({ appointment }: CommunicationLogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [agreement, setAgreement] = useState<CommunicationAgreement | null>(null);
  const { t } = useTranslation();

  const handleToggleLog = (e: React.MouseEvent) => {
    e.stopPropagation();

    setIsOpen(!isOpen);
  };

  const handleSelectAgreement = (agreement: CommunicationAgreement) => {
    setAgreement(agreement);
  };

  const handlePrint = () =>
    printJS({
      printable: "CommunicationLog-Content",
      type: "html",
      copyStyles: true,
      css: ["./CommunicationLog.scss"],
      scanStyles: false,
      targetStyles: ["*"]
    });

  const tabs = useMemo(() => {
    const panes: TabPaneType[] = [];

    appointment.customer_communication?.events?.forEach(event => {
      switch (event.type) {
        case COMMUNICATION_EVENTS.OnlineCheckInSent:
          panes.push({
            menuItem: t("v8_online_check_in", "Online Check-in"),
            render: () => <TabContent tab={COMMUNICATION_LOG_TAB.OnlineCheckin} id={event.id} appointment={appointment} onSelectAgreement={handleSelectAgreement} />
          });
          break;

        case COMMUNICATION_EVENTS.DiagnoseOverviewSent:
          panes.push({
            menuItem: t("v8_diagnose_overview", "Diagnose Overview"),
            render: () => <TabContent tab={COMMUNICATION_LOG_TAB.DiagnoseOverview} id={event.id} appointment={appointment} onSelectAgreement={handleSelectAgreement} />
          });
          break;

        case COMMUNICATION_EVENTS.DiagnoseOverviewResent:
          panes.push({
            menuItem: t("v8_diagnose_overview_update", "Diagnose Overview Update"),
            render: () => (
              <TabContent tab={COMMUNICATION_LOG_TAB.DiagnoseOverviewUpdate} id={event.id} appointment={appointment} onSelectAgreement={handleSelectAgreement} />
            )
          });
          break;

        case COMMUNICATION_EVENTS.RepairOverviewSent:
          panes.push({
            menuItem: t("v8_repair_overview", "Repair overview"),
            render: () => <TabContent tab={COMMUNICATION_LOG_TAB.RepairOverview} id={event.id} appointment={appointment} />
          });
          break;

        default:
          break;
      }
    });

    return panes;
  }, [appointment.customer_communication]);

  return (
    <>
      <div className="CommunicationLog-button" onClick={handleToggleLog}>
        <DetailsCard title={t("v8_customer_comm_log", "Customer Communication Log")} icon="user" rightContent={null} showExpandArrow={false} button />
      </div>
      <Modal closeOnDimmerClick={false} open={isOpen} onOpen={handleToggleLog} onClose={handleToggleLog} className="CommunicationLog-Modal">
        <Modal.Header>
          <div>
            <Icon className="list" />
            {t("v8_customer_comm_log", "Customer communication log")}
          </div>

          <div className="modal-header-actions">
            <Button onClick={handlePrint} className="modal-print-btn" color="grey">
              {t("v8_print", "Print")}
              <Icon className="print" />
            </Button>

            <Button className="modal-close-btn" basic onClick={handleToggleLog}>
              <Icon className="xmark" />
            </Button>
          </div>
        </Modal.Header>
        <Modal.Content id="CommunicationLog-Content" scrolling>
          <AppointmentInfo appointment={appointment} />
          <CommunicationLogReplacementCar appointment={appointment} />
          {tabs.length > 0 && <Tab menu={{ secondary: true }} panes={tabs} />}
        </Modal.Content>
      </Modal>
      <AgreementModal isOpen={!!agreement} onClose={() => setAgreement(null)} agreement={agreement} />
    </>
  );
};
